/**
 * Home page: Session hub for managing dice capture sessions
 *
 * Displays an inline session form (code + name) and lists saved sessions.
 * Any session code auto-creates a session on the backend if it doesn't exist.
 */

import { ChevronDown, ChevronUp, RefreshCw, X } from 'lucide-react';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FrogMascot } from '@/components/FrogMascot';
import { PageHeader } from '@/components/PageHeader';
import { SessionCard } from '@/components/SessionCard';
import { SessionQrCode } from '@/components/SessionQrCode';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle
} from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import { PAGE_TITLES } from '@/constants/terminology';
import { useSessionActions } from '@/hooks/useSessionActions';
import { generateSessionCode, buildJoinUrl } from '@/lib/sessionCode';
import { extractZodFieldErrors } from '@/lib/utils';
import { sessionFormSchema } from '@/schemas/session';
import { createScopedReporter } from '@/services/errorReporter';
import { getEndpointIdsForSession } from '@/services/sessionAdapter';
import { useActiveConnectionsStore } from '@/stores/activeConnectionsStore';
import { useAuthStore } from '@/stores/authStore';
import { useSessionsStore } from '@/stores/sessionsStore';

const reporter = createScopedReporter('SESSION');

/** Sessions welcome drawer — explains what sessions are for first-time visitors */
function SessionsWelcomeDrawer() {
  const [open, setOpen] = useState(() => !localStorage.getItem('sessions-welcome-dismissed'));

  function handleDismiss() {
    localStorage.setItem('sessions-welcome-dismissed', 'true');
    setOpen(false);
  }

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerContent>
        <DrawerHeader className="relative text-center">
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-2 top-2 h-8 w-8"
            onClick={() => setOpen(false)}
            aria-label="Close welcome dialog"
          >
            <X className="h-4 w-4" />
          </Button>
          <div className="flex justify-center mb-4 mt-2">
            <FrogMascot variant="waiting" size="lg" />
          </div>
          <DrawerTitle>Welcome to Sessions</DrawerTitle>
        </DrawerHeader>
        <div className="px-4 space-y-3 text-sm text-muted-foreground">
          <p>
            A <span className="font-semibold text-foreground">session</span> connects your phone camera to your online game. Share the session code with your VTT or Discord bot, then roll your real dice — Dice Frog reads the result and sends it automatically.
          </p>
          <p>
            Create a session below with any code you like, or use the one we generated. Add your player name so your group knows whose roll it is.
          </p>
        </div>
        <DrawerFooter>
          <Button variant="accent" onClick={handleDismiss} enablePressEffect className="w-full">
            Got It
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

/**
 * Home page displaying session form and session cards
 */
export function HomePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { sessions, addSession } = useSessionsStore();
  const connections = useActiveConnectionsStore((s) => s.connections);
  const { handleConnect, handleDisconnect, handleDelete } = useSessionActions();
  const { isAuthenticated, user } = useAuthStore();

  // Sort connected/connecting sessions to the top for quick access
  const sortedSessions = useMemo(() => {
    return [...sessions].sort((a, b) => {
      const aActive = getEndpointIdsForSession(a.id).some((id) => {
        const s = connections.get(id);
        return s?.status === 'connected' || s?.status === 'connecting';
      });
      const bActive = getEndpointIdsForSession(b.id).some((id) => {
        const s = connections.get(id);
        return s?.status === 'connected' || s?.status === 'connecting';
      });
      if (aActive && !bActive) return -1;
      if (!aActive && bActive) return 1;
      return 0;
    });
  }, [sessions, connections]);

  const [sessionCode, setSessionCode] = useState('');
  const [playerName, setPlayerName] = useState('');
  const [showQrCode, setShowQrCode] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  /** Auto-generate session code on mount, or pre-fill from URL */
  useEffect(() => {
    const codeFromUrl = searchParams.get('code');
    if (codeFromUrl) {
      setSessionCode(codeFromUrl.toUpperCase());
      // Clear the URL param after consuming it
      setSearchParams({}, { replace: true });
    } else {
      setSessionCode(generateSessionCode());
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Generate a new random session code
   */
  function handleGenerateCode() {
    setSessionCode(generateSessionCode());
  }

  /** Use the auth username when logged in, otherwise the form field */
  const effectivePlayerName = isAuthenticated && user ? user.username : playerName;

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      const result = sessionFormSchema.safeParse({ sessionCode, playerName: effectivePlayerName });
      if (!result.success) {
        setErrors(extractZodFieldErrors(result.error));
        return;
      }

      setErrors({});

      try {
        const sessionId = addSession({
          sessionCode: result.data.sessionCode,
          playerName: result.data.playerName.trim()
        });
        reporter.info('Session created successfully');
        // Reset form for next session
        setSessionCode(generateSessionCode());
        setPlayerName('');
        // Connect immediately after creating the session
        handleConnect(sessionId);
      } catch (error) {
        if (error instanceof Error && error.message.includes('already exists')) {
          // Session with this code already saved — reconnect to it
          const code = result.data.sessionCode.toUpperCase();
          const existing = sessions.find(
            (s) => s.config.sessionCode.toUpperCase() === code
          );
          if (existing) {
            handleConnect(existing.id);
            return;
          }
        }
        const message = error instanceof Error ? error.message : 'Failed to create session';
        setErrors({ sessionCode: message });
        reporter.warning(message);
      }
    },
    [sessionCode, effectivePlayerName, addSession, handleConnect, sessions]
  );

  return (
    <div className="flex h-full flex-col p-4 overflow-y-auto">
      <div className="w-full max-w-7xl mx-auto flex flex-col flex-1 min-h-0">
        {/* Header */}
        <PageHeader
          variant="waiting"
          title={PAGE_TITLES.HOME}
        />
        <p className="text-muted-foreground mb-6">Enter a session code and your name to get started</p>

        {/*
         * Session list comes before the form because users return to existing
         * sessions far more often than they create new ones.
         */}
        <div className="flex-1 mb-6">
          {sessions.length === 0 ? (
            <Card variant="elevated">
              <CardContent className="flex flex-col items-center justify-center py-10 text-center">
                <h3 className="text-lg font-semibold mb-1">No sessions yet</h3>
                <p className="text-sm text-muted-foreground">
                  Create your first session below to start capturing dice rolls.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sortedSessions.map((session, index) => (
                <div
                  key={session.id}
                  style={{ animationDelay: `${index * 50}ms` }}
                  className="animate-hop-appear"
                >
                  <SessionCard
                    session={session}
                    onConnect={handleConnect}
                    onDisconnect={handleDisconnect}
                    onDelete={handleDelete}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Session Form */}
        <Card variant="elevated" className="mb-6">
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Session Code */}
              <div className="space-y-1.5">
                <label htmlFor="session-code" className="text-sm font-medium">
                  Session Code
                </label>
                <div className="flex gap-2">
                  <Input
                    id="session-code"
                    placeholder="ABC123"
                    value={sessionCode}
                    onChange={(e) => setSessionCode(e.target.value.toUpperCase())}
                    maxLength={10}
                    className="font-mono uppercase"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleGenerateCode}
                    aria-label="Generate random session code"
                    title="Generate random code"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
                {errors.sessionCode && (
                  <p className="text-xs text-destructive">{errors.sessionCode}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Share this code with players so they can join your session.
                </p>

                {/* QR Code — collapsed by default */}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowQrCode(!showQrCode)}
                  className="text-xs text-muted-foreground px-0"
                >
                  {showQrCode ? (
                    <>
                      <ChevronUp className="h-3.5 w-3.5 mr-1" />
                      Hide QR Code
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-3.5 w-3.5 mr-1" />
                      Show QR Code
                    </>
                  )}
                </Button>
                {showQrCode && <SessionQrCode url={buildJoinUrl(sessionCode)} size={160} />}
              </div>

              {/* Player Name — hidden when logged in (uses auth username) */}
              {isAuthenticated && user ? (
                <p className="text-xs text-muted-foreground">
                  Playing as <span className="font-medium text-primary">{user.username}</span>
                </p>
              ) : (
                <div className="space-y-1.5">
                  <label htmlFor="player-name" className="text-sm font-medium">
                    Your Name
                  </label>
                  <Input
                    id="player-name"
                    placeholder="Your name"
                    value={playerName}
                    onChange={(e) => setPlayerName(e.target.value)}
                    maxLength={50}
                  />
                  {errors.playerName && (
                    <p className="text-xs text-destructive">{errors.playerName}</p>
                  )}
                </div>
              )}

              {/* Submit */}
              <Button
                type="submit"
                variant="accent"
                className="w-full"
                enablePressEffect
              >
                Go
              </Button>
            </form>
          </CardContent>
        </Card>

        <SessionsWelcomeDrawer />
      </div>
    </div>
  );
}
